// material
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TablePagination,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { useState, useMemo } from 'react';

// components
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import { UserListHead } from '../sections/@dashboard/user';
import { Search, SearchIconWrapper, StyledInputBase } from '../components/SearchInput';

// store
import { api as stockApi } from '../store/domains/stock';
import { ROLE_OPTIONS, ROWS_PER_PAGE_OPTIONS } from '../constants/dealer';

const TABLE_HEAD = [
  { id: 'id', label: 'ID', alignRight: false },
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'surname', label: 'Surname', alignRight: false },
  { id: 'role', label: 'Role', alignRight: false },
  { id: 'dealerCode', label: 'Dealer Code', alignRight: false },
  { id: 'username', label: 'Username', alignRight: false },
  { id: 'contactNumbers', label: 'Contact Numbers', alignRight: false },
  { id: 'emails', label: 'Emails', alignRight: false },
  { id: 'actions', label: '', alignRight: false },
];

function MultiValueCell({ values }) {
  if (!values?.length) return '-';

  return (
    <Stack spacing={0.5}>
      {values.map((value) => (
        <span key={value}>{value}</span>
      ))}
    </Stack>
  );
}

const cleanList = (values) => values.map((value) => value.trim()).filter(Boolean);

function MultiValueField({ label, addLabel, values, onChange }) {
  const handleItemChange = (index, value) => onChange(values.map((item, i) => (i === index ? value : item)));

  const handleRemove = (index) => onChange(values.filter((_, i) => i !== index));

  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {values.map((value, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Stack direction="row" spacing={1} alignItems="center" key={index}>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            value={value}
            onChange={(e) => handleItemChange(index, e.target.value)}
          />
          <Tooltip title="Remove">
            <IconButton size="small" onClick={() => handleRemove(index)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => onChange([...values, ''])}
        sx={{ alignSelf: 'flex-start' }}
      >
        {addLabel}
      </Button>
    </Stack>
  );
}

export default function DealerStaffList() {
  const [titleInput, setTitleInput] = useState('');
  const [pinInput, setPinInput] = useState('');
  const [usernameInput, setUsernameInput] = useState('');
  const [submittedParams, setSubmittedParams] = useState(undefined);

  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [editUsername, setEditUsername] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editContactNumbers, setEditContactNumbers] = useState([]);
  const [editEmails, setEditEmails] = useState([]);

  const {
    data: staffData,
    isLoading: staffLoading,
    isFetching,
  } = stockApi.useGetDealerStaff(submittedParams, {
    skip: !submittedParams,
  });

  const [editDealerStaff, { isLoading: editLoading }] = stockApi.useEditDealerStaff();

  const filteredStaff = useMemo(() => {
    if (!staffData) return [];
    const lower = searchValue.toLowerCase();
    return staffData.filter(
      (s) =>
        s.name?.toLowerCase().includes(lower) ||
        s.surname?.toLowerCase().includes(lower) ||
        s.username?.toLowerCase().includes(lower) ||
        String(s.id).includes(lower)
    );
  }, [staffData, searchValue]);

  const paginatedStaff = useMemo(
    () => filteredStaff.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredStaff, page, rowsPerPage]
  );

  const canFetch = titleInput.trim() || pinInput.trim() || usernameInput.trim();

  const handleFetchStaff = () => {
    if (!canFetch) return;
    const params = {};
    if (titleInput.trim()) params.title = titleInput.trim();
    if (pinInput.trim()) params.pin = pinInput.trim();
    if (usernameInput.trim()) params.userName = usernameInput.trim();
    setPage(0);
    setSearchValue('');
    setSubmittedParams(params);
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setPage(0);
  };

  const handleOpenEditDialog = (staff) => {
    setSelectedStaff(staff);
    setEditUsername(staff.username);
    setEditRole(staff.role);
    setEditContactNumbers(staff.contactNumbers ?? []);
    setEditEmails(staff.emails ?? []);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedStaff(null);
    setEditUsername('');
    setEditRole('');
    setEditContactNumbers([]);
    setEditEmails([]);
  };

  const handleEditSubmit = async () => {
    if (!selectedStaff) return;
    const payload = {
      staffId: selectedStaff.id,
      id: selectedStaff.id,
      pin: selectedStaff.pin,
      name: selectedStaff.name,
      surname: selectedStaff.surname,
      status: selectedStaff.status,
      username: editUsername.trim(),
      role: editRole,
    };

    const contactNumbers = cleanList(editContactNumbers);
    if (contactNumbers.length) payload.contactNumbers = contactNumbers;

    const emails = cleanList(editEmails);
    if (emails.length) payload.emails = emails;

    try {
      await editDealerStaff(payload).unwrap();
      handleCloseEditDialog();
    } catch {
      handleCloseEditDialog();
    }
  };

  const renderTableBody = () => {
    if (staffLoading) {
      return (
        <TableRow>
          <TableCell colSpan={TABLE_HEAD.length + 1} align="center">
            <CircularProgress size={28} />
          </TableCell>
        </TableRow>
      );
    }

    if (paginatedStaff.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={TABLE_HEAD.length + 1} align="center">
            No staff found
          </TableCell>
        </TableRow>
      );
    }

    return paginatedStaff.map((row) => {
      const { id, name, surname, role, username, dealerCode, contactNumbers, emails } = row;
      return (
        <TableRow hover key={id} tabIndex={-1}>
          <TableCell padding="checkbox" />
          <TableCell>{id}</TableCell>
          <TableCell>{name}</TableCell>
          <TableCell>{surname}</TableCell>
          <TableCell>{role}</TableCell>
          <TableCell>{dealerCode}</TableCell>
          <TableCell>{username}</TableCell>
          <TableCell>
            <MultiValueCell values={contactNumbers} />
          </TableCell>
          <TableCell>
            <MultiValueCell values={emails} />
          </TableCell>
          <TableCell align="center">
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => handleOpenEditDialog(row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <Page>
      <Container>
        <Stack direction="row" alignItems="center" gap={2} mb={4}>
          <TextField
            label="Title"
            variant="outlined"
            size="small"
            value={titleInput}
            onChange={(e) => setTitleInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleFetchStaff()}
            placeholder="Enter dealer title"
          />
          <TextField
            label="PIN"
            variant="outlined"
            size="small"
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleFetchStaff()}
            placeholder="Enter PIN"
          />
          <TextField
            label="Username"
            variant="outlined"
            size="small"
            value={usernameInput}
            onChange={(e) => setUsernameInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleFetchStaff()}
            placeholder="Enter username"
          />
          <Button variant="contained" onClick={handleFetchStaff} disabled={!canFetch}>
            Fetch Staff
          </Button>
        </Stack>

        {submittedParams && (
          <>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Search>
                <SearchIconWrapper>
                  <SearchIcon />
                </SearchIconWrapper>
                <StyledInputBase
                  onChange={handleSearchChange}
                  value={searchValue}
                  placeholder="Search staff…"
                  inputProps={{ 'aria-label': 'search staff' }}
                />
              </Search>
            </Stack>
            <Card>
              {(isFetching || staffLoading) && <LinearProgress />}
              <Scrollbar>
                <TableContainer sx={{ minWidth: 800 }}>
                  <Table>
                    <UserListHead headLabel={TABLE_HEAD} />
                    <TableBody>{renderTableBody()}</TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>
              <TablePagination
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                component="div"
                count={filteredStaff.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </Card>
          </>
        )}
        <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Staff Member</DialogTitle>
          <DialogContent>
            <Stack spacing={3} mt={1}>
              <TextField
                label="Username"
                variant="outlined"
                size="small"
                fullWidth
                value={editUsername}
                onChange={(e) => setEditUsername(e.target.value)}
              />
              <Select
                value={editRole}
                onChange={(e) => setEditRole(e.target.value)}
                size="small"
                fullWidth
                displayEmpty
              >
                {ROLE_OPTIONS.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
              <MultiValueField
                label="Contact Numbers"
                addLabel="Add contact number"
                values={editContactNumbers}
                onChange={setEditContactNumbers}
              />
              <MultiValueField label="Emails" addLabel="Add email" values={editEmails} onChange={setEditEmails} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog} disabled={editLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleEditSubmit}
              variant="contained"
              disabled={editLoading || !editUsername.trim() || !editRole}
            >
              {editLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
